import React, { useCallback, useEffect, useState } from "react";

import AutorenewIcon from "@mui/icons-material/Autorenew";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import LockIcon from "@mui/icons-material/Lock";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import type {
  LlmGatewayBinding,
  LlmGatewayCredentials,
} from "../api/llmGatewayTypes";
import { useLlmGatewayApi } from "../api/useLlmGatewayApi";

interface Props {
  orgAlias: string;
  viewState?: "loaded" | "loading" | "error" | "forbidden";
  onBack: () => void;
}

const APPROVED_SCOPES = ["model:ollama:glm-5.2"];
const PLATFORM_ENVIRONMENT =
  import.meta.env.VITE_PLATFORM_ENVIRONMENT ??
  getPlatformEnvironment(import.meta.env.VITE_API_URL);

function getPlatformEnvironment(apiUrl?: string): string | undefined {
  if (!apiUrl) return import.meta.env.DEV ? "dev" : undefined;
  if (apiUrl.includes("dev-api")) return "dev";
  if (apiUrl.includes("staging-api")) return "staging";
  if (apiUrl.includes("api.rusha.")) return "production";
  return undefined;
}

export default function LlmGatewayManagementPage({
  orgAlias,
  viewState = "loaded",
  onBack,
}: Props) {
  const api = useLlmGatewayApi();
  const [binding, setBinding] = useState<LlmGatewayBinding | null>(null);
  const [credentials, setCredentials] = useState<LlmGatewayCredentials | null>(
    null,
  );
  const [tab, setTab] = useState(0);
  const [dialog, setDialog] = useState<
    "activate" | "credentials" | "rotate" | "revoke" | null
  >(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const loadBinding = useCallback(async () => {
    setActionError(null);
    try {
      const bindings = await api.list(orgAlias);
      setBinding(
        bindings.find(
          (candidate) => candidate.serviceAlias === "llm-gateway",
        ) ?? null,
      );
    } catch (error) {
      setActionError(
        error instanceof Error
          ? error.message
          : "Unable to load gateway configuration.",
      );
    }
  }, [api, orgAlias]);

  useEffect(() => {
    if (viewState === "loaded") void loadBinding();
  }, [loadBinding, viewState]);

  useEffect(
    () => () => {
      setCredentials(null);
    },
    [],
  );

  useEffect(() => {
    if (!credentials) return;
    const timer = window.setTimeout(() => {
      setCredentials(null);
      setNotice(
        "Credentials were hidden after the secure viewing period ended.",
      );
    }, 60_000);
    return () => window.clearTimeout(timer);
  }, [credentials]);

  useEffect(() => {
    if (
      !binding ||
      !["pending", "provisioning", "rotating"].includes(binding.status)
    ) {
      return;
    }
    const timer = window.setTimeout(() => void loadBinding(), 1500);
    return () => window.clearTimeout(timer);
  }, [binding, loadBinding]);

  const status = binding?.status ?? "not_activated";
  const isReady = status === "ready";
  // The API remains the authorization source of truth. The browser session
  // uses httpOnly cookies, so the UI cannot reliably determine the org role.
  const canManage = true;

  const runAction = async (
    action: () => Promise<LlmGatewayBinding>,
    successMessage: string,
  ) => {
    setActionError(null);
    setNotice(null);
    try {
      const result = await action();
      setBinding(result);
      setDialog(null);
      setCredentials(null);
      setNotice(successMessage);
    } catch (error) {
      setActionError(
        error instanceof Error ? error.message : "The gateway action failed.",
      );
    }
  };

  const activate = async () => {
    if (!PLATFORM_ENVIRONMENT) {
      setActionError(
        "The platform environment is unavailable. Activation cannot continue.",
      );
      return;
    }
    await runAction(
      () =>
        api.create(
          {
            serviceAlias: "llm-gateway",
            environment: PLATFORM_ENVIRONMENT,
            audience: "llm-gateway",
            requestedScopes: APPROVED_SCOPES,
            idempotencyKey: `llm-gateway-${orgAlias}-${PLATFORM_ENVIRONMENT}`,
          },
          orgAlias,
        ),
      "LLM Gateway activation started.",
    );
  };

  const revealCredentials = async () => {
    if (!binding) return;
    setActionError(null);
    try {
      const result = await api.getCredentials({
        alias: orgAlias,
        bindingId: String(binding.id),
      });
      setCredentials(result);
      setDialog(null);
      setNotice(
        "Credentials loaded for this session. Hide them when finished.",
      );
    } catch (error) {
      setActionError(
        error instanceof Error
          ? error.message
          : "Unable to retrieve credentials.",
      );
    }
  };

  const copyValue = async (label: string, value: string) => {
    await navigator.clipboard.writeText(value);
    setCopied(label);
    window.setTimeout(() => setCopied(null), 1500);
  };

  const downloadEnv = () => {
    if (!credentials) return;
    const content = [
      `RUSHA_LLM_GATEWAY_URL=${credentials.gatewayUrl}`,
      `RUSHA_LLM_GATEWAY_CLIENT_ID=${credentials.clientId}`,
      `RUSHA_LLM_GATEWAY_CLIENT_SECRET=${credentials.clientSecret}`,
      `RUSHA_LLM_GATEWAY_TOKEN_ENDPOINT=${credentials.tokenEndpoint}`,
      `RUSHA_LLM_GATEWAY_AUDIENCE=${credentials.audience}`,
      `RUSHA_LLM_GATEWAY_SCOPES=${credentials.scopes.join(" ")}`,
      "",
    ].join("\n");
    const url = URL.createObjectURL(
      new Blob([content], { type: "text/plain" }),
    );
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "rusha-llm-gateway.env";
    anchor.click();
    URL.revokeObjectURL(url);
    setNotice(
      "Credential file downloaded. Store it in a secure secret manager.",
    );
  };

  if (viewState === "loading") {
    return (
      <Stack
        spacing={2}
        role="status"
        aria-label="Loading gateway configuration"
      >
        <Skeleton variant="text" width={320} height={48} />
        <Skeleton variant="rounded" height={220} />
        <Skeleton variant="rounded" height={180} />
      </Stack>
    );
  }

  if (viewState === "forbidden") {
    return (
      <StateCard title="Permission required" onBack={onBack}>
        Organization administrator access is required to manage LLM Gateway
        configuration and credentials.
      </StateCard>
    );
  }

  if (viewState === "error") {
    return (
      <StateCard title="Gateway configuration unavailable" onBack={onBack}>
        The gateway configuration could not be loaded. Retry after the service
        recovers.
      </StateCard>
    );
  }

  const statusLabel =
    status === "not_activated" ? "Activation required" : status;

  return (
    <>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
        >
          <Stack spacing={1}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box sx={markSx}>✦</Box>
              <Stack spacing={0.5}>
                <Typography
                  variant="overline"
                  fontWeight={700}
                  letterSpacing="0.08em"
                >
                  {isReady ? "Active Rusha product" : "Rusha product"}
                </Typography>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Typography variant="h4" component="h1" fontWeight={700}>
                    LLM Gateway
                  </Typography>
                  <Chip
                    label={statusLabel}
                    aria-live="polite"
                    color={
                      isReady
                        ? "success"
                        : status === "failed"
                          ? "error"
                          : "warning"
                    }
                    variant="outlined"
                    size="small"
                  />
                </Stack>
              </Stack>
            </Stack>
            <Typography variant="body1" color="text.secondary" maxWidth={720}>
              Organization-scoped access to approved language-model backends.
            </Typography>
          </Stack>
          <Stack spacing={0.5}>
            <DetailRow label="Organization" value={orgAlias} />
            <DetailRow label="Scope" value="Organization" />
            <DetailRow
              label="Environment"
              value={
                binding?.environment ??
                PLATFORM_ENVIRONMENT ??
                "Platform-derived"
              }
            />
            <DetailRow label="Product instance" value="LLM Gateway" />
            <DetailRow label="Product status" value={statusLabel} />
          </Stack>
        </Stack>

        {(actionError || api.error) && (
          <Alert severity="error" onClose={() => setActionError(null)}>
            {actionError ?? api.error}
          </Alert>
        )}
        {notice && <Alert severity="success">{notice}</Alert>}

        {!binding || status === "not_activated" ? (
          <ActivationCard
            disabled={!PLATFORM_ENVIRONMENT}
            onActivate={() => setDialog("activate")}
          />
        ) : (
          <>
            {status === "failed" && (
              <Alert severity="error">
                {binding.failureMessage ??
                  "Provisioning failed. Retry activation."}
              </Alert>
            )}
            <Tabs
              value={tab}
              onChange={(_, value: number) => setTab(value)}
              aria-label="LLM Gateway management"
              variant="scrollable"
              allowScrollButtonsMobile
            >
              {[
                "Overview",
                "Integrations",
                "Credentials",
                "Usage",
                "Settings",
                "Audit",
              ].map((label) => (
                <Tab
                  key={label}
                  label={label}
                  id={`gateway-tab-${label.toLowerCase()}`}
                />
              ))}
            </Tabs>
            {tab === 0 && (
              <OverviewPanel
                binding={binding}
                credentials={credentials}
                onCredentials={() => setDialog("credentials")}
                onRotate={() => setDialog("rotate")}
                onRevoke={() => setDialog("revoke")}
                onCopy={copyValue}
                copied={copied}
                canManage={canManage && isReady}
              />
            )}
            {tab === 1 && <IntegrationPanel />}
            {tab === 2 && (
              <CredentialsPanel
                binding={binding}
                credentials={credentials}
                onReveal={() => setDialog("credentials")}
                onDownload={downloadEnv}
                onCopy={copyValue}
                copied={copied}
                onRotate={() => setDialog("rotate")}
                canManage={canManage && isReady}
              />
            )}
            {tab === 3 && (
              <InfoPanel
                title="Usage"
                body="Usage reporting will appear here when gateway meters are available."
              />
            )}
            {tab === 4 && (
              <InfoPanel
                title="Gateway settings"
                body="Model providers and request policy are managed by Rusha Platform."
              />
            )}
            {tab === 5 && (
              <InfoPanel
                title="Audit activity"
                body="Credential and lifecycle actions are recorded by the API audit stream."
              />
            )}
          </>
        )}
        <Button
          variant="outlined"
          onClick={onBack}
          sx={{ textTransform: "none", alignSelf: "flex-start" }}
        >
          Back to products
        </Button>
      </Stack>

      <Dialog
        open={dialog !== null}
        onClose={() => setDialog(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {dialog === "activate" && "Activate LLM Gateway?"}
          {dialog === "credentials" && "Reveal gateway credentials?"}
          {dialog === "rotate" && "Rotate gateway credentials?"}
          {dialog === "revoke" && "Revoke gateway access?"}
        </DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            {dialog === "activate" &&
              `This provisions organization-scoped access for ${orgAlias}. The platform environment is ${PLATFORM_ENVIRONMENT ?? "not configured"}.`}
            {dialog === "credentials" &&
              "Only organization administrators should reveal these values. The secret will be available only for this browser session."}
            {dialog === "rotate" &&
              "Rotation preserves the client ID and scopes but replaces the client secret. Existing tokens may remain valid until expiry."}
            {dialog === "revoke" &&
              "Revocation removes gateway access and deletes stored credentials. Applications using this binding will stop obtaining tokens."}
          </Typography>
          {dialog === "credentials" && (
            <TextField
              sx={{ mt: 2 }}
              fullWidth
              label="Approved scopes"
              value={APPROVED_SCOPES.join(", ")}
              InputProps={{ readOnly: true }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          {dialog === "activate" && (
            <Button variant="contained" onClick={() => void activate()}>
              Activate
            </Button>
          )}
          {dialog === "credentials" && (
            <Button
              variant="contained"
              onClick={() => void revealCredentials()}
            >
              Reveal credentials
            </Button>
          )}
          {dialog === "rotate" && binding && (
            <Button
              variant="contained"
              onClick={() =>
                void runAction(
                  () =>
                    api.rotate({
                      alias: orgAlias,
                      bindingId: String(binding.id),
                    }),
                  "Credential rotation started.",
                )
              }
            >
              Rotate credentials
            </Button>
          )}
          {dialog === "revoke" && binding && (
            <Button
              color="error"
              variant="contained"
              onClick={() =>
                void runAction(
                  () =>
                    api.revoke({
                      alias: orgAlias,
                      bindingId: String(binding.id),
                    }),
                  "Gateway access revoked.",
                )
              }
            >
              Revoke access
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}

function ActivationCard({
  disabled,
  onActivate,
}: {
  disabled: boolean;
  onActivate: () => void;
}) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="h6" fontWeight={700}>
            Activate LLM Gateway
          </Typography>
          <Typography color="text.secondary">
            Activate organization-scoped OIDC access. No application or project
            selection is required.
          </Typography>
          <Stack spacing={1}>
            {[
              "Confirm organization scope",
              "Review the approved model scope",
              "Retrieve credentials after provisioning",
            ].map((step, index) => (
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                key={step}
              >
                <Chip label={index + 1} size="small" color="primary" />
                <Typography variant="body2">{step}</Typography>
              </Stack>
            ))}
          </Stack>
          {!import.meta.env.VITE_PLATFORM_ENVIRONMENT && (
            <Alert severity="warning">
              Platform environment is unavailable; activation is disabled.
            </Alert>
          )}
          <Button
            variant="contained"
            disabled={disabled}
            onClick={onActivate}
            sx={{ alignSelf: "flex-start" }}
          >
            Activate LLM Gateway
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

function OverviewPanel({
  binding,
  credentials,
  onCredentials,
  onRotate,
  onRevoke,
  onCopy,
  copied,
  canManage,
}: {
  binding: LlmGatewayBinding;
  credentials: LlmGatewayCredentials | null;
  onCredentials: () => void;
  onRotate: () => void;
  onRevoke: () => void;
  onCopy: (label: string, value: string) => Promise<void>;
  copied: string | null;
  canManage: boolean;
}) {
  return (
    <Stack spacing={2}>
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={1.5}>
            <Typography variant="h6" fontWeight={700}>
              Connection configuration
            </Typography>
            <DetailRow label="Client ID" value={binding.keycloakClientId} />
            <DetailRow label="Audience" value={binding.audience} />
            <DetailRow
              label="Scopes"
              value={binding.requestedScopes.join(", ")}
            />
            {credentials && (
              <>
                <Divider />
                <DetailRow label="Gateway URL" value={credentials.gatewayUrl} />
                <DetailRow
                  label="Token endpoint"
                  value={credentials.tokenEndpoint}
                />
                <Button
                  startIcon={
                    copied === "gateway" ? undefined : <ContentCopyIcon />
                  }
                  onClick={() => void onCopy("gateway", credentials.gatewayUrl)}
                  sx={{ alignSelf: "flex-start", textTransform: "none" }}
                >
                  {copied === "gateway" ? "Copied" : "Copy gateway URL"}
                </Button>
              </>
            )}
            <Typography variant="body2" color="text.secondary">
              Exchange the client credentials at the token endpoint, request the
              approved scope, then send the bearer token to the gateway.
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <Button
                variant="contained"
                disabled={!canManage || !credentials}
                onClick={onCredentials}
              >
                Manage credentials
              </Button>
              <Button
                startIcon={<AutorenewIcon />}
                disabled={!canManage}
                onClick={onRotate}
              >
                Rotate
              </Button>
              <Button
                color="error"
                startIcon={<DeleteOutlineIcon />}
                disabled={!canManage}
                onClick={onRevoke}
              >
                Revoke
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}

function IntegrationPanel() {
  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Integration guidance
              </Typography>
              <Typography color="text.secondary">
                LLM Gateway access is organization-scoped and does not register
                customer applications.
              </Typography>
            </Box>
            <Chip label="Read-only" variant="outlined" size="small" />
          </Stack>
          <Stack spacing={1.25}>
            <DetailRow label="Application selection" value="Not required" />
            <DetailRow
              label="Project or stack binding"
              value="Not created by activation"
            />
            <DetailRow
              label="Customer identity"
              value="Separate product boundary"
            />
            <DetailRow
              label="Connection metadata"
              value="Available after activation"
            />
          </Stack>
          <Alert severity="info">
            Customer application identity and Rusha human login remain separate
            from gateway credentials.
          </Alert>
        </Stack>
      </CardContent>
    </Card>
  );
}

function CredentialsPanel({
  binding,
  credentials,
  onReveal,
  onDownload,
  onCopy,
  copied,
  onRotate,
  canManage,
}: {
  binding: LlmGatewayBinding;
  credentials: LlmGatewayCredentials | null;
  onReveal: () => void;
  onDownload: () => void;
  onCopy: (label: string, value: string) => Promise<void>;
  copied: string | null;
  onRotate: () => void;
  canManage: boolean;
}) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" fontWeight={700}>
              Credentials
            </Typography>
            <LockIcon color="action" />
          </Stack>
          <Typography color="text.secondary">
            Values are retrieved only after an explicit administrator action.
            They are never included in binding metadata.
          </Typography>
          {credentials ? (
            <Stack spacing={1.5}>
              <SecretRow
                label="Client ID"
                value={credentials.clientId}
                onCopy={() => onCopy("client-id", credentials.clientId)}
                copied={copied === "client-id"}
              />
              <SecretRow
                label="Client secret"
                value={credentials.clientSecret}
                secret
              />
              <SecretRow
                label="Token endpoint"
                value={credentials.tokenEndpoint}
                onCopy={() =>
                  onCopy("token-endpoint", credentials.tokenEndpoint)
                }
                copied={copied === "token-endpoint"}
              />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <Button startIcon={<DownloadIcon />} onClick={onDownload}>
                  Download .env
                </Button>
                <Button
                  startIcon={<AutorenewIcon />}
                  onClick={onRotate}
                  disabled={!canManage}
                >
                  Rotate credentials
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Button
              variant="contained"
              onClick={onReveal}
              disabled={!canManage || binding.status !== "ready"}
            >
              Reveal credentials
            </Button>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

function SecretRow({
  label,
  value,
  secret,
  onCopy,
  copied,
}: {
  label: string;
  value: string;
  secret?: boolean;
  onCopy?: () => void;
  copied?: boolean;
}) {
  const [visible, setVisible] = useState(false);
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <TextField
        fullWidth
        size="small"
        label={label}
        value={secret && !visible ? "••••••••••••••••" : value}
        InputProps={{ readOnly: true }}
      />
      {secret && (
        <Button onClick={() => setVisible((current) => !current)}>
          {visible ? "Hide" : "Show"}
        </Button>
      )}
      {onCopy && (
        <Tooltip title={copied ? "Copied" : "Copy"}>
          <IconButton aria-label={`Copy ${label}`} onClick={onCopy}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
}

function InfoPanel({ title, body }: { title: string; body: string }) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" fontWeight={700} gutterBottom>
          {title}
        </Typography>
        <Typography color="text.secondary">{body}</Typography>
      </CardContent>
    </Card>
  );
}

function DetailRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <Stack
      direction="row"
      spacing={2}
      justifyContent="space-between"
      alignItems="center"
    >
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography
        component="div"
        variant="body2"
        fontWeight={600}
        textAlign="right"
      >
        {value}
      </Typography>
    </Stack>
  );
}

function StateCard({
  title,
  onBack,
  children,
}: {
  title: string;
  onBack: () => void;
  children: React.ReactNode;
}) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Stack
          spacing={2}
          alignItems="center"
          sx={{ textAlign: "center", py: 4 }}
        >
          <Typography variant="h5" fontWeight={700}>
            {title}
          </Typography>
          <Typography color="text.secondary" maxWidth={480}>
            {children}
          </Typography>
          <Button variant="outlined" onClick={onBack}>
            Back to product catalog
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

const markSx = {
  width: 52,
  height: 52,
  borderRadius: "14px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "1.375rem",
  bgcolor: "rgba(37, 99, 235, 0.2)",
  border: "1px solid rgba(96, 165, 250, 0.35)",
  color: "#bfdbfe",
  flexShrink: 0,
};
